import React, { useCallback, useEffect, useState } from 'react';
import { money } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  DescriptionList,
  Drawer,
  EmptyState,
  Input,
  KpiTile,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  Timeline,
  useToast,
} from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { suppliersApi } from '../suppliers/suppliersApi';
import ApprovalChain from '../approvals/ApprovalChain';
import AttachmentsCard from '../attachments/AttachmentsCard';
import CommentComposer from '../comments/CommentComposer';
import { buildCommentTimelineItem, useCommentAnchor } from '../comments/CommentTimeline';
import { useAuth } from '../../auth/AuthContext';
import { purchaseOrderPath, requisitionPath, goodsReceiptPath } from '../../lib/slug';
import { receivingApi, discrepancyReasonLabel, grnDiscrepancyMeta } from '../receiving/receivingApi';
import { purchaseOrdersApi, poStatusMeta, deliveryStatusMeta, DELIVERY_STATUSES } from './purchaseOrdersApi';
import '../requisitions/requisitions.css';
import './purchase-orders.css';

const hasMoney = (m) => m && Number(m.amount) > 0;

const ACTIVITY_META = {
  Created: { tone: 'success', icon: <Icon.Sparkles size={14} /> },
  Updated: { tone: 'default', icon: <Icon.Pencil size={14} /> },
  Submitted: { tone: 'primary', icon: <Icon.ArrowUpCircle size={14} /> },
  Approved: { tone: 'success', icon: <Icon.Check size={14} /> },
  Rejected: { tone: 'danger', icon: <Icon.X size={14} /> },
  Sent: { tone: 'brand', icon: <Icon.Mail size={14} /> },
  Received: { tone: 'success', icon: <Icon.ArrowDownCircle size={14} /> },
  Cancelled: { tone: 'warning', icon: <Icon.Ban size={14} /> },
  Closed: { tone: 'success', icon: <Icon.Check size={14} /> },
  Commented: { tone: 'default', icon: <Icon.MessageSquare size={14} /> },
  DeliveryUpdated: { tone: 'brand', icon: <Icon.Truck size={14} /> },
};

export default function PurchaseOrderDetailPage() {
  const { id: routeRef } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const { hasPermission, canActOnStepRole, properties } = useProperty();
  const { user } = useAuth();
  const canCreate = hasPermission('purchaseorders.create');
  const canApprove = hasPermission('purchaseorders.approve');
  const canSend = hasPermission('purchaseorders.send');
  const canCancel = hasPermission('purchaseorders.cancel');
  const canReceive = hasPermission('receiving.record');
  const canReturn = hasPermission('returns.manage');

  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);

  const [decision, setDecision] = useState(null); // 'approve' | 'reject'
  const [comment, setComment] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [shortCloseOpen, setShortCloseOpen] = useState(false);
  const [shortCloseReason, setShortCloseReason] = useState('');

  const [activity, setActivity] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const anchorId = useCommentAnchor(activity);
  const [receipts, setReceipts] = useState([]);

  // Send-to-supplier dialog
  const [sendOpen, setSendOpen] = useState(false);
  const [sendRecipient, setSendRecipient] = useState('');
  const [sendCc, setSendCc] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [sendPrefilling, setSendPrefilling] = useState(false);

  // Delivery-status drawer
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({
    deliveryStatus: '', expectedDate: '', carrierName: '', trackingReference: '', note: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPo(await purchaseOrdersApi.get(routeRef));
    } catch (err) {
      setError(err?.message || t('Purchase order not found.'));
    } finally {
      setLoading(false);
    }
  }, [routeRef, t]);

  const loadActivity = useCallback(async (poId) => {
    try {
      const res = await purchaseOrdersApi.listActivity(poId, { pageSize: 100 });
      setActivity(res.items || []);
    } catch { /* non-fatal */ }
  }, []);

  const loadReceipts = useCallback(async (poId) => {
    try {
      const res = await receivingApi.list({ purchaseOrderId: poId, pageSize: 100, sortBy: 'received', descending: true });
      const summaries = res.items || [];
      // Fetch full detail so we can show what was received line-by-line, not just totals.
      const detailed = await Promise.all(summaries.map(async (g) => {
        try { return await receivingApi.get(g.id); } catch { return g; }
      }));
      setReceipts(detailed);
    } catch { setReceipts([]); }
  }, []);

  const loadLookups = useCallback(async () => {
    try {
      const [us, locs] = await Promise.all([catalogApi.listUnits(), catalogApi.listLocations()]);
      setUnits(us || []);
      setLocations(locs || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { load(); loadLookups(); }, [load, loadLookups]);

  // Pull activity once the order resolves, and canonicalise the URL to the readable serial.
  useEffect(() => {
    if (!po?.id) return;
    loadActivity(po.id);
    loadReceipts(po.id);
    const canonical = purchaseOrderPath(po);
    if (`/purchase-orders/${routeRef}` !== canonical) {
      navigate(canonical, { replace: true });
    }
  }, [po, routeRef, loadActivity, loadReceipts, navigate]);

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;
  const locationName = (lid) => locations.find((l) => l.id === lid)?.name;

  const act = async (fn, successMsg) => {
    setBusy(true);
    try {
      const updated = await fn();
      setPo(updated);
      toast.success(successMsg);
      loadActivity(updated.id);
    } catch (err) {
      toast.error(t('Action failed'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const submit = () => act(() => purchaseOrdersApi.submit(po.id), t('Submitted for approval'));
  const doCancel = async () => { setCancelOpen(false); await act(() => purchaseOrdersApi.cancel(po.id), t('Purchase order cancelled')); };
  const doShortClose = async () => {
    setShortCloseOpen(false);
    await act(() => purchaseOrdersApi.shortClose(po.id, shortCloseReason.trim() || null), t('Order short-closed'));
    setShortCloseReason('');
  };

  // Open the send dialog and prefill the recipient from the supplier's primary contact / email.
  const openSend = async () => {
    setSendRecipient(po.sentToEmail || '');
    setSendCc('');
    setSendMessage('');
    setSendOpen(true);
    if (!po.supplierId || po.sentToEmail) return;
    setSendPrefilling(true);
    try {
      const supplier = await suppliersApi.get(po.supplierId);
      const contacts = supplier?.contacts || [];
      const primary = contacts.find((c) => c.isPrimary && c.email);
      const anyContact = contacts.find((c) => c.email);
      setSendRecipient((primary?.email || anyContact?.email || supplier?.email || '').trim());
    } catch { /* non-fatal — user can type the address */ } finally {
      setSendPrefilling(false);
    }
  };

  const confirmSend = async () => {
    const recipient = sendRecipient.trim();
    if (!recipient) { toast.error(t('Enter a recipient email address.')); return; }
    const cc = sendCc.split(',').map((s) => s.trim()).filter(Boolean);
    setSendOpen(false);
    await act(
      () => purchaseOrdersApi.send(po.id, { recipientEmail: recipient, cc, message: sendMessage.trim() || null }),
      t('Order emailed to {recipient}', { recipient }),
    );
  };

  const openDelivery = () => {
    setDeliveryForm({
      deliveryStatus: po.deliveryStatus && po.deliveryStatus !== 'None' ? po.deliveryStatus : 'AwaitingConfirmation',
      expectedDate: po.expectedDate ? po.expectedDate.slice(0, 10) : '',
      carrierName: po.carrierName || '',
      trackingReference: po.trackingReference || '',
      note: '',
    });
    setDeliveryOpen(true);
  };

  const confirmDelivery = async () => {
    if (!deliveryForm.deliveryStatus) { toast.error(t('Choose a delivery status.')); return; }
    setDeliveryOpen(false);
    await act(
      () => purchaseOrdersApi.updateDeliveryStatus(po.id, {
        deliveryStatus: deliveryForm.deliveryStatus,
        expectedDate: deliveryForm.expectedDate || null,
        carrierName: deliveryForm.carrierName.trim() || null,
        trackingReference: deliveryForm.trackingReference.trim() || null,
        note: deliveryForm.note.trim() || null,
      }),
      t('Delivery status updated'),
    );
  };

  const confirmDecision = async () => {
    if (decision === 'reject' && !comment.trim()) { toast.error(t('A reason is required to reject.')); return; }
    const mode = decision;
    setDecision(null);
    if (mode === 'approve') await act(() => purchaseOrdersApi.approve(po.id, comment.trim()), t('Purchase order approved'));
    else await act(() => purchaseOrdersApi.reject(po.id, comment.trim()), t('Purchase order rejected'));
    setComment('');
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await purchaseOrdersApi.remove(po.id);
      toast.success(t('Purchase order deleted'));
      navigate('/purchase-orders');
    } catch (err) {
      toast.error(t('Could not delete'), { description: err?.message });
      setBusy(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }
  if (error || !po) {
    return (
      <PageContainer size="full">
        <EmptyState icon={<Icon.CircleAlert size={28} />} title={t("Couldn't load purchase order")} description={error || t('Not found.')}
          action={<Button variant="secondary" onClick={() => navigate('/purchase-orders')}>{t('Back to purchase orders')}</Button>} />
      </PageContainer>
    );
  }

  const statusMeta = poStatusMeta(po.status);
  const isDraft = po.status === 'Draft';
  const isPending = po.status === 'PendingApproval';
  const isApproved = po.status === 'Approved';
  const canDelete = ['Draft', 'Rejected', 'Cancelled'].includes(po.status);
  const cancellable = ['Draft', 'PendingApproval', 'Approved'].includes(po.status);
  // Approval is the commitment: approved orders are receivable and trackable even if the
  // optional supplier email was never sent.
  const receivable = ['Approved', 'Sent', 'PartiallyReceived'].includes(po.status);
  const shortClosable = ['Sent', 'PartiallyReceived'].includes(po.status);
  const editable = ['Draft', 'PendingApproval'].includes(po.status);
  const canTrackDelivery = canCreate || canSend || canReceive;
  const deliveryMeta = deliveryStatusMeta(po.deliveryStatus);
  const returnable = ['PartiallyReceived', 'Closed'].includes(po.status)
    && (po.lines || []).some((l) => Number(l.receivedQuantity || 0) > 0);

  const steps = po.approvalSteps || [];
  const hasChain = steps.length > 0;
  const currentStep = hasChain ? steps.find((s) => s.order === po.currentApprovalStepOrder) : null;
  const canActNow = isPending && canApprove && (!currentStep || canActOnStepRole(currentStep.roleId));

  const propertyName = po.propertyId
    ? (properties.find((p) => p.id === po.propertyId)?.name || null)
    : null;

  const overviewItems = [
    { term: t('Property'), description: propertyName || <span className="pp-cell-muted">{t('All properties')}</span> },
    { term: t('Status'), description: <Badge tone={statusMeta.tone} dot>{t(statusMeta.label)}</Badge> },
    ...(deliveryMeta ? [{
      term: t('Delivery'),
      description: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <Badge tone={deliveryMeta.tone} dot>{t(deliveryMeta.label)}</Badge>
          {(po.carrierName || po.trackingReference || po.deliveryStatusUpdatedByName) && (
            <span className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>
              {[
                po.carrierName,
                po.trackingReference,
                po.deliveryStatusUpdatedByName
                  ? t('updated by {name}', { name: po.deliveryStatusUpdatedByName })
                  : null,
              ].filter(Boolean).join(' · ')}
            </span>
          )}
        </span>
      ),
    }] : []),
    { term: t('Supplier'), description: po.supplierName || <span className="pp-cell-muted">{t('Not assigned')}</span> },
    { term: t('Deliver to'), description: locationName(po.locationId) || '—' },
    { term: t('Expected'), description: po.expectedDate ? new Date(po.expectedDate).toLocaleDateString() : '—' },
    {
      term: t('Source requisition'),
      description: po.sourceRequisitionId
        ? <Button variant="link" size="sm" onClick={() => navigate(requisitionPath({ number: po.sourceRequisitionNumber, id: po.sourceRequisitionId }))}>{po.sourceRequisitionNumber}</Button>
        : '—',
    },
    { term: t('Raised by'), description: po.createdByName || '—' },
    { term: t('Created'), description: new Date(po.createdAt).toLocaleString() },
    { term: po.status === 'Sent' ? t('Sent') : t('Submitted'), description: po.sentAt ? new Date(po.sentAt).toLocaleString() : (po.submittedAt ? new Date(po.submittedAt).toLocaleString() : '—') },
    ...((po.lines || []).find((l) => l.glAccountCode) ? [{ term: t('GL account'), description: (po.lines || []).find((l) => l.glAccountCode).glAccountCode }] : []),
    ...((po.lines || []).find((l) => l.costCenter) ? [{ term: t('Cost center'), description: (po.lines || []).find((l) => l.costCenter).costCenter }] : []),
  ];

  const attachmentsBase = `/purchase-orders/${po.id}/attachments`;
  const activityItems = activity.map((a) => {
    if (a.type === 'Commented') {
      return buildCommentTimelineItem({
        activity: a,
        apiBasePath: `/purchase-orders/${po.id}`,
        attachmentsBasePath: attachmentsBase,
        currentUserId: user?.id,
        editingCommentId,
        setEditingCommentId,
        onChanged: () => loadActivity(po.id),
        highlightId: anchorId,
        t,
      });
    }

    const meta = ACTIVITY_META[a.type] || { tone: 'default', icon: <Icon.Activity size={14} /> };
    const detail = a.metadata?.reason || a.metadata?.comment;
    return {
      id: a.id,
      tone: meta.tone,
      icon: meta.icon,
      title: `${a.actorName || t('Someone')} ${a.summary}`,
      meta: new Date(a.createdAt).toLocaleString(),
      description: detail ? `“${detail}”` : undefined,
    };
  });

  const everReceived = receipts.length > 0 || ['PartiallyReceived', 'Closed'].includes(po.status);
  const poLines = po.lines || [];
  const anyDiscount = poLines.some((l) => Number(l.discountPercent) > 0);
  const anyTax = poLines.some((l) => Number(l.taxPercent) > 0);

  const lineColumns = [
    { key: 'description', header: t('Description'), width: 240, render: (l) => (
      <div>
        <div style={{ fontWeight: 600 }}>{l.description}</div>
        {l.sku && <div className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>{l.sku}</div>}
      </div>
    ) },
    { key: 'qty', header: t('Qty'), width: 70, align: 'right', render: (l) => l.quantity },
    { key: 'uom', header: t('Unit'), width: 70, render: (l) => uomCode(l.uomId) || <span className="pp-cell-muted">—</span> },
    ...(everReceived ? [{
      key: 'received', header: t('Received'), width: 110, align: 'right',
      render: (l) => {
        const rq = Number(l.receivedQuantity || 0);
        const full = rq >= Number(l.quantity);
        return (
          <Badge tone={rq === 0 ? 'neutral' : full ? 'success' : 'warning'}>
            {rq} / {l.quantity}
          </Badge>
        );
      },
    }] : []),
    { key: 'unit', header: t('Unit price'), width: 110, align: 'right', render: (l) => money(l.unitPrice) },
    ...(anyDiscount ? [{ key: 'disc', header: t('Disc %'), width: 70, align: 'right', render: (l) => (Number(l.discountPercent) > 0 ? `${Number(l.discountPercent)}%` : <span className="pp-cell-muted">—</span>) }] : []),
    ...(anyTax ? [{ key: 'tax', header: t('Tax %'), width: 70, align: 'right', render: (l) => (Number(l.taxPercent) > 0 ? `${Number(l.taxPercent)}%` : <span className="pp-cell-muted">—</span>) }] : []),
    { key: 'total', header: t('Net amount'), width: 120, align: 'right', render: (l) => money(l.lineTotal) },
  ];

  const totalsRows = [
    { label: 'Subtotal', value: money(po.subtotal), show: true },
    { label: 'Discount', value: `−${money(po.discountTotal)}`, show: hasMoney(po.discountTotal) },
    { label: 'Tax', value: money(po.taxTotal), show: hasMoney(po.taxTotal) },
    { label: 'Freight', value: money(po.freightAmount), show: hasMoney(po.freightAmount) },
    { label: 'Other charges', value: money(po.otherCharges), show: hasMoney(po.otherCharges) },
  ].filter((r) => r.show);

  const renderReceipt = (g) => {
    const lines = g.lines || [];
    const disc = grnDiscrepancyMeta(g.discrepancyStatus);
    return (
      <div className="po-receipt" key={g.id}>
        <div className="po-receipt__head">
          <div className="po-receipt__id">
            <Button variant="link" size="sm" onClick={() => navigate(goodsReceiptPath(g))}>{g.number}</Button>
            {g.hasOverReceipt && <Badge tone="warning">{t('Over-receipt')}</Badge>}
            {g.discrepancyStatus && g.discrepancyStatus !== 'None' && <Badge tone={disc.tone}>{t(disc.label === 'Open' ? 'Discrepancy open' : 'Discrepancy resolved')}</Badge>}
          </div>
          <div className="po-receipt__meta">
            {t('Received {date}', { date: g.receivedDate ? new Date(g.receivedDate).toLocaleDateString() : '—' })}
            {' · '}{t('recorded {timestamp}', { timestamp: g.createdAt ? new Date(g.createdAt).toLocaleString() : '—' })}
            {' · '}{t('by {name}', { name: g.receivedByName || t('unknown') })}
            {' · '}{money(g.total)}
          </div>
        </div>
        <div className="po-receipt__lines">
          {lines.length === 0 ? (
            <div className="po-receipt__line"><span className="pp-cell-muted">{t('No line detail.')}</span></div>
          ) : lines.map((l) => {
            const uom = uomCode(l.uomId) ? ` ${uomCode(l.uomId)}` : '';
            const reasonLabel = discrepancyReasonLabel(l.discrepancyReason);
            const reason = reasonLabel ? t(reasonLabel) : null;
            return (
              <div className="po-receipt__line" key={l.lineId}>
                <div className="po-receipt__line-item">
                  <div className="po-receipt__line-name">{l.description}</div>
                  <div className="po-receipt__line-sku">
                    {l.sku || ''}
                    {reason ? `${l.sku ? ' · ' : ''}${reason}` : ''}
                    {l.notes ? ` · “${l.notes}”` : ''}
                  </div>
                </div>
                <div className="po-receipt__line-qty">
                  <div>
                    <span className="po-receipt__qty-label">{t('Accepted')}</span>
                    <strong>{Number(l.receivedQuantity || 0)}{uom}</strong>
                  </div>
                  <div>
                    <span className="po-receipt__qty-label">{t('Rejected')}</span>
                    {Number(l.rejectedQuantity || 0) > 0
                      ? <Badge tone="danger">{Number(l.rejectedQuantity)}{uom}</Badge>
                      : <strong className="pp-cell-muted">0</strong>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {g.notes && <div className="po-receipt__note">{t('Note:')} {g.notes}</div>}
      </div>
    );
  };

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/purchase-orders')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Purchase Orders')}
      </Button>

      <PageHeader
        title={po.title}
        description={
          <span>
            <span className="req-detail__number">{po.number}</span>
            {propertyName && <> · {propertyName}</>}
          </span>
        }
        icon={<Icon.ShoppingCart size={22} />}
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <Button variant="ghost" leadingIcon={<Icon.Printer size={15} />} onClick={() => navigate(purchaseOrderPath(po, '/print'))}>{t('Print')}</Button>
            {isDraft && canCreate && (
              <Button variant="primary" loading={busy} leadingIcon={<Icon.ArrowUpCircle size={15} />} onClick={submit}>{t('Submit')}</Button>
            )}
            {editable && canCreate && (
              <Button variant="secondary" leadingIcon={<Icon.Pencil size={15} />} onClick={() => navigate(purchaseOrderPath(po, '/edit'))}>{t('Edit')}</Button>
            )}
            {isPending && canApprove && canActNow && (
              <>
                <Button variant="primary" loading={busy} leadingIcon={<Icon.Check size={15} />} onClick={() => { setComment(''); setDecision('approve'); }}>{t('Approve')}</Button>
                <Button variant="secondary" loading={busy} leadingIcon={<Icon.X size={15} />} onClick={() => { setComment(''); setDecision('reject'); }}>{t('Reject')}</Button>
              </>
            )}
            {receivable && canReceive && (
              <Button variant="primary" leadingIcon={<Icon.ArrowDownCircle size={15} />} onClick={() => navigate(purchaseOrderPath(po, '/receive'))}>{t('Receive goods')}</Button>
            )}
            {receivable && canTrackDelivery && (
              <Button variant="secondary" loading={busy} leadingIcon={<Icon.Truck size={15} />} onClick={openDelivery}>{t('Update delivery')}</Button>
            )}
            {/* Emailing the supplier is an optional convenience, never the main action. */}
            {isDraft && canSend && (
              <Button variant="ghost" loading={busy} leadingIcon={<Icon.Mail size={15} />} onClick={openSend}>{t('Email draft')}</Button>
            )}
            {isApproved && canSend && (
              <Button variant="ghost" loading={busy} leadingIcon={<Icon.Mail size={15} />} onClick={openSend}>{t('Email to supplier')}</Button>
            )}
            {po.status === 'Sent' && canSend && (
              <Button variant="ghost" loading={busy} leadingIcon={<Icon.Mail size={15} />} onClick={openSend}>{t('Re-send')}</Button>
            )}
            {returnable && canReturn && (
              <Button variant="secondary" leadingIcon={<Icon.ArrowUpCircle size={15} />} onClick={() => navigate(purchaseOrderPath(po, '/return'))}>{t('Return goods')}</Button>
            )}
            {shortClosable && canCancel && (
              <Button variant="ghost" loading={busy} onClick={() => setShortCloseOpen(true)}>{t('Short-close')}</Button>
            )}
            {cancellable && canCancel && (
              <Button variant="ghost" loading={busy} onClick={() => setCancelOpen(true)}>{t('Cancel')}</Button>
            )}
            {canDelete && canCreate && (
              <IconButton aria-label={t('Delete purchase order')} variant="ghost" onClick={() => setDeleteOpen(true)}><Icon.Trash size={16} /></IconButton>
            )}
          </div>
        }
      />

      {isPending && currentStep && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone={canActNow ? 'info' : 'warning'}>
            {t('Step {order} of {total}: awaiting {role} approval.', { order: currentStep.order, total: steps.length, role: currentStep.roleName || t('approver') })}
            {' '}{canActNow ? t('You can approve or reject this step.') : t('You do not hold the role required for this step.')}
          </Banner>
        </div>
      )}
      {!po.supplierId && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="warning">{t('No supplier is assigned yet. Edit the order to choose a supplier before submitting.')}</Banner>
        </div>
      )}
      {po.status === 'Rejected' && po.decisionComment && (
        <div style={{ marginBottom: 'var(--space-4)' }}><Banner tone="danger">{t('Rejected:')} {po.decisionComment}</Banner></div>
      )}
      {isApproved && (
        <div style={{ marginBottom: 'var(--space-4)' }}><Banner tone="success">
          {t('Approved and final. Record deliveries with “Receive goods” — emailing the order to the supplier is optional.')}
        </Banner></div>
      )}
      {po.status === 'Sent' && (
        <div style={{ marginBottom: 'var(--space-4)' }}><Banner tone="success">{po.sentAt
          ? t('Emailed to {recipient} on {date}.', { recipient: po.sentToEmail || po.supplierName || t('the supplier'), date: new Date(po.sentAt).toLocaleDateString() })
          : t('Emailed to {recipient}.', { recipient: po.sentToEmail || po.supplierName || t('the supplier') })}</Banner></div>
      )}
      {po.status === 'PartiallyReceived' && (
        <div style={{ marginBottom: 'var(--space-4)' }}><Banner tone="warning">{t('Partially received. Record further deliveries with “Receive goods” to close the order.')}</Banner></div>
      )}
      {po.status === 'Closed' && (
        <div style={{ marginBottom: 'var(--space-4)' }}><Banner tone="success">{t('Fully received and closed.')}</Banner></div>
      )}

      <div className="req-kpis">
        <KpiTile label={t('Order total')} value={money(po.total)} />
        <KpiTile label={t('Lines')} value={po.lines?.length || 0} />
        <KpiTile label={t('Supplier')} value={po.supplierName || '—'} />
        <KpiTile label={t('Status')} value={t(statusMeta.label)} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Card>
          <Card.Body>
            <DescriptionList items={overviewItems} layout="stacked" columns={2} />
          </Card.Body>
        </Card>

        {po.notes && (
          <Card>
            <Card.Header><Card.Title>{t('Notes')}</Card.Title></Card.Header>
            <Card.Body>{po.notes}</Card.Body>
          </Card>
        )}

        <Card>
          <Card.Header><Card.Title>{t('Lines')}</Card.Title></Card.Header>
          <Card.Body>
            <DataTable columns={lineColumns} data={poLines} rowKey={(l) => l.lineId} density="comfortable" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-4)' }}>
              <div className="po-totals">
                {totalsRows.map((r) => (
                  <div className="po-totals__row" key={r.label}>
                    <span>{t(r.label)}</span>
                    <span className="po-totals__row-value">{r.value}</span>
                  </div>
                ))}
                <div className="po-totals__divider" />
                <div className="po-totals__row po-totals__row--grand">
                  <span>{t('Order total')}</span>
                  <span className="po-totals__row-value">{money(po.total)}</span>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {receipts.length > 0 && (
          <Card>
            <Card.Header>
              <Card.Title>{t('Goods receipts')}</Card.Title>
              <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                {receipts.length === 1 ? t('1 delivery recorded') : t('{count} deliveries recorded', { count: receipts.length })} {t('against this order — what was accepted, rejected, when and by whom.')}
              </div>
            </Card.Header>
            <Card.Body>
              <div className="po-receipts">
                {receipts.map(renderReceipt)}
              </div>
            </Card.Body>
          </Card>
        )}

        {hasChain && (
          <Card>
            <Card.Header><Card.Title>{t('Approval chain')}</Card.Title></Card.Header>
            <Card.Body>
              <ApprovalChain
                steps={steps}
                currentStepOrder={po.currentApprovalStepOrder}
                currency={po.currency}
                showSignatures
              />
            </Card.Body>
          </Card>
        )}

        {(po.submittedAt || (!hasChain && po.decidedByName)) && (
          <Card>
            <Card.Header><Card.Title>{t('Authorisation')}</Card.Title></Card.Header>
            <Card.Body>
              <div className="req-sign__grid">
                {po.submittedAt && (
                  <div className="req-sign__block">
                    <span className="req-sign__role">{t('Raised by')}</span>
                    <div className="req-sign__mark">
                      {po.submittedSignature
                        ? <img src={po.submittedSignature} alt={t('Buyer signature')} />
                        : <span className="req-sign__unsigned">{t('— not signed —')}</span>}
                    </div>
                    <span className="req-sign__name">{po.createdByName || '—'}</span>
                    <span className="req-sign__meta">{new Date(po.submittedAt).toLocaleString()}</span>
                  </div>
                )}
                {!hasChain && po.decidedByName && (
                  <div className="req-sign__block">
                    <span className="req-sign__role">{po.status === 'Rejected' ? t('Rejected by') : t('Approved by')}</span>
                    <div className="req-sign__mark">
                      {po.decisionSignature
                        ? <img src={po.decisionSignature} alt={t('Approver signature')} />
                        : <span className="req-sign__unsigned">{t('— not signed —')}</span>}
                    </div>
                    <span className="req-sign__name">{po.decidedByName}</span>
                    <span className="req-sign__meta">{po.decidedAt ? new Date(po.decidedAt).toLocaleString() : ''}</span>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        )}

        <AttachmentsCard basePath={`/purchase-orders/${po.id}/attachments`} canManage={canCreate} />

        <Card>
          <Card.Header><Card.Title>{t('Activity')}</Card.Title></Card.Header>
          <Card.Body>
            <CommentComposer
              basePath={`/purchase-orders/${po.id}`}
              onPosted={() => loadActivity(po.id)}
            />
            {activityItems.length === 0 ? (
              <EmptyState icon={<Icon.Activity size={26} />} title={t('No activity yet')} description={t('Lifecycle events and comments will appear here.')} />
            ) : (
              <Timeline items={activityItems} className="req-activity__timeline" />
            )}
          </Card.Body>
        </Card>
      </div>

      <Drawer
        open={!!decision}
        onClose={() => (busy ? null : setDecision(null))}
        title={decision === 'approve' ? t('Approve purchase order') : t('Reject purchase order')}
        description={decision === 'approve' ? t('Optionally add a note for the buyer.') : t('Tell the buyer why this is being rejected.')}
        size="sm"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <Textarea
            label={decision === 'approve' ? t('Comment (optional)') : t('Reason')}
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            autoFocus
          />
          <div className="req-form__actions">
            <Button variant="ghost" onClick={() => setDecision(null)} disabled={busy}>{t('Cancel')}</Button>
            <Button variant={decision === 'approve' ? 'primary' : 'secondary'} loading={busy} onClick={confirmDecision}>
              {decision === 'approve' ? t('Approve') : t('Reject')}
            </Button>
          </div>
        </div>
      </Drawer>

      <Drawer
        open={sendOpen}
        onClose={() => (busy ? null : setSendOpen(false))}
        title={isDraft ? t('Email draft for review') : t('Send order to supplier')}
        description={isDraft
          ? t('The draft is emailed clearly marked as “not a confirmed order”, so the supplier can review pricing and availability. The order stays a draft.')
          : t('The full purchase order is emailed to the recipient below.')}
        size="sm"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <Input
            label={t('Recipient')}
            type="email"
            placeholder={sendPrefilling ? t('Looking up supplier contact…') : 'supplier@example.com'}
            value={sendRecipient}
            onChange={(e) => setSendRecipient(e.target.value)}
            autoFocus
          />
          <Input
            label={t('CC (optional)')}
            placeholder={t('Comma-separated email addresses')}
            value={sendCc}
            onChange={(e) => setSendCc(e.target.value)}
            hint={t('Separate multiple addresses with commas.')}
          />
          <Textarea
            label={t('Message (optional)')}
            rows={4}
            value={sendMessage}
            onChange={(e) => setSendMessage(e.target.value)}
            placeholder={t('Add a note to the supplier (appears at the top of the email).')}
          />
          <div className="req-form__actions">
            <Button variant="ghost" onClick={() => setSendOpen(false)} disabled={busy}>{t('Cancel')}</Button>
            <Button variant="primary" loading={busy} leadingIcon={<Icon.Mail size={15} />} onClick={confirmSend}>
              {isDraft ? t('Email draft') : t('Send order')}
            </Button>
          </div>
        </div>
      </Drawer>

      <Drawer
        open={deliveryOpen}
        onClose={() => (busy ? null : setDeliveryOpen(false))}
        title={t('Update delivery status')}
        description={t('Track where the shipment stands. Every update is recorded in the activity log.')}
        size="sm"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div className="ps-field">
            <span className="ps-field__label">{t('Delivery status')}</span>
            <Select
              options={DELIVERY_STATUSES.map((s) => ({ value: s.value, label: t(s.label) }))}
              value={deliveryForm.deliveryStatus}
              onChange={(v) => setDeliveryForm((f) => ({ ...f, deliveryStatus: v }))}
              ariaLabel={t('Delivery status')}
            />
          </div>
          <Input
            label={t('Expected delivery date')}
            type="date"
            value={deliveryForm.expectedDate}
            onChange={(e) => setDeliveryForm((f) => ({ ...f, expectedDate: e.target.value }))}
          />
          <Input
            label={t('Carrier (optional)')}
            placeholder={t('e.g. JNE, DHL, supplier truck')}
            value={deliveryForm.carrierName}
            onChange={(e) => setDeliveryForm((f) => ({ ...f, carrierName: e.target.value }))}
          />
          <Input
            label={t('Tracking reference (optional)')}
            placeholder={t('Tracking or consignment number')}
            value={deliveryForm.trackingReference}
            onChange={(e) => setDeliveryForm((f) => ({ ...f, trackingReference: e.target.value }))}
          />
          <Textarea
            label={t('Note (optional)')}
            rows={3}
            value={deliveryForm.note}
            onChange={(e) => setDeliveryForm((f) => ({ ...f, note: e.target.value }))}
            placeholder={t('e.g. customs cleared, arriving Friday morning')}
          />
          <div className="req-form__actions">
            <Button variant="ghost" onClick={() => setDeliveryOpen(false)} disabled={busy}>{t('Cancel')}</Button>
            <Button variant="primary" loading={busy} leadingIcon={<Icon.Truck size={15} />} onClick={confirmDelivery}>{t('Update status')}</Button>
          </div>
        </div>
      </Drawer>

      <ConfirmDialog
        open={cancelOpen}
        title={t('Cancel purchase order')}
        description={t("Cancelling stops this order from being approved or issued. This can't be undone.")}
        confirmLabel={t('Cancel order')}
        tone="danger"
        loading={busy}
        onConfirm={doCancel}
        onCancel={() => (busy ? null : setCancelOpen(false))}
      />

      <ConfirmDialog
        open={shortCloseOpen}
        title={t('Short-close purchase order')}
        description={t("Close this order without receiving the outstanding quantity. Already-received items are kept; no further deliveries can be recorded. Use this when the supplier won't fulfil the remainder.")}
        confirmLabel={t('Short-close order')}
        tone="danger"
        loading={busy}
        onConfirm={doShortClose}
        onCancel={() => (busy ? null : setShortCloseOpen(false))}
      >
        <Textarea
          rows={2}
          value={shortCloseReason}
          onChange={(e) => setShortCloseReason(e.target.value)}
          placeholder={t('Reason (optional) — e.g. supplier discontinued the item')}
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={deleteOpen}
        title={t('Delete purchase order')}
        description={t("Permanently delete {number}? This can't be undone.", { number: po.number })}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={busy}
        onConfirm={doDelete}
        onCancel={() => (busy ? null : setDeleteOpen(false))}
      />
    </PageContainer>
  );
}
